import { useQuery } from "@tanstack/react-query";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { fetchProjectsEngagements } from "PFCore/services/booking/fetch_projects_engagements";
import { fetchProjectsRoles } from "PFCore/services/booking/fetch_projects_roles";
import {
  ActivityWorkflowState,
  AvailabilityRule,
  Filters,
  Id,
  OrderOption,
  PaginatedCollection,
  QueryOptions,
  RollforwardInfo,
  Value
} from "PFTypes";

import { projectsKeys } from "./query_keys";

export type ProjectItemReviewer = {
  id: number;
  unbiased: boolean;
};

export type ProjectItemActivity = {
  availability?: AvailabilityRule;
  id: number;
  name: string;
  postUntil: string;
  childrenWorkflowStatesTally?: Record<string, number>;
  templateId: number;
  vacancies?: {
    closed: number;
    total: number;
    shortlists: Record<number, { total: number; booked: number; confirmed: number }>; // stats per vacancyId
  };
  state: "draft" | "new" | "complete";
  workflowState: ActivityWorkflowState;
  parentActivity: { name: string; id: number; templateId: number };
  duration?: {
    startDate: string;
    endDate: string;
  };
  hasBookings: boolean;
  bookable?: boolean;
  assignee?: { id: number; firstName?: string; lastName?: string };
  principalOwner?: { id: number; firstName?: string; lastName?: string };
  numberOfDays?: number;
  reviewers: ProjectItemReviewer[];
  bookingCategoryId?: Id | null;
  coowned: boolean;
  rollforwardInfo?: RollforwardInfo;
};

export type ProjectItem = {
  activity: ProjectItemActivity;
  id: number;
  optionalFields: Record<string, any>;
};

export type ProjectsResponse = PaginatedCollection<ProjectItem[]>;

type UseProjectsEntries = {
  page: number;
  calendarRoot: CalendarRoot;
  perPage: number;
  order: OrderOption | null;
  filters: Filters<Value> | undefined;
  options: QueryOptions<ProjectsResponse>;
};

export const useProjectsEntries = ({
  page = 1,
  calendarRoot,
  perPage = 15,
  order,
  filters,
  options = {}
}: UseProjectsEntries) =>
  useQuery({
    queryKey: projectsKeys.withParameters(calendarRoot, filters, order, page, perPage),
    queryFn: () => {
      const params = { page, perPage, order: order ?? undefined, filters };
      return calendarRoot === CalendarRoot.ProjectsRoles
        ? fetchProjectsRoles(params)
        : fetchProjectsEngagements(params);
    },
    ...options
  });
